import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, collection, serverTimestamp, setDoc, getDocs, addDoc, query, where } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessProfile } from '../../services/businessService';
import { generateProposalPDF } from '../../services/pdfService';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Timestamp } from 'firebase/firestore';
import { FaArrowLeft, FaPlus, FaTimes, FaUser, FaSearch, FaSpinner, FaFileAlt, FaTools, FaBox, FaClipboardList, FaCalculator, FaPercent, FaClock, FaReceipt, FaExclamationCircle, FaSave, FaFilePdf, FaCheck } from 'react-icons/fa';
import { db } from '../../config/firebase';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface QuotationData {
  id: string;
  clientName: string;
  clientEmail: string;
  createdAt: Timestamp;
  status: string;
  '1750100522588'?: string;
  '1750100532828'?: string;
  customFields?: any[];
  [key: string]: any;
}

const QuotationScreen: React.FC = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [quotation, setQuotation] = useState<QuotationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('services');
  const [creatingProject, setCreatingProject] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  const [businessServices, setBusinessServices] = useState<any[]>([]);
  const [loadingBusinessServices, setLoadingBusinessServices] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedServices, setSelectedServices] = useState<any[]>([]);
  const [newService, setNewService] = useState<any>({
    name: '',
    description: '',
    price: '',
    priceType: 'fixed',
    quantity: 1
  });
  const [isAddingService, setIsAddingService] = useState(false);
  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState<any[]>([]);
  const [newMaterial, setNewMaterial] = useState<any>({
    name: '',
    description: '',
    price: '',
    priceType: 'fixed',
    quantity: 1,
    unit: 'unidad'
  });
  const [isAddingMaterial, setIsAddingMaterial] = useState(false);
  const [businessTaxes, setBusinessTaxes] = useState<any[]>([]);
  const [selectedTaxes, setSelectedTaxes] = useState<any[]>([]);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [workDuration, setWorkDuration] = useState<number>(0);
  const [workDurationType, setWorkDurationType] = useState<'hours' | 'days'>('days');
  const [workStartDate, setWorkStartDate] = useState<string>('');
  const [quotationValidityDate, setQuotationValidityDate] = useState<string>('');
  const [isSavingProposal, setIsSavingProposal] = useState(false);
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchQuotation = async () => {
      console.log('QuotationScreen mounted with ID:', quotationId);
      console.log('User data:', userData);

      if (!quotationId) {
        console.error('No quotation ID provided');
        setError('ID de cotización no proporcionado');
        setLoading(false);
        return;
      }

      if (!userData?.businessId) {
        console.error('No business ID available');
        setError('ID de negocio no disponible');
        setLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotationId);
        console.log('Fetching quotation from path:', `quotations/${userData.businessId}/submissions/${quotationId}`);
        
        const quotationDoc = await getDoc(quotationRef);

        if (!quotationDoc.exists()) {
          console.error('Quotation document does not exist');
          setError('Cotización no encontrada');
          return;
        }

        const data = quotationDoc.data();
        console.log('Quotation data retrieved:', data);
        
        // Actualizar el estado a "read" si está "unread" o "pending"
        if (data.status === 'unread' || data.status === 'pending') {
          try {
            const now = new Date();
            const userName = `${(userData as any).firstName || ''} ${(userData as any).lastName || ''}`.trim() || (userData as any).email || currentUser?.email;
            const currentLog = data.activityLog || [];

            await updateDoc(quotationRef, {
              status: 'read',
              readAt: now,
              lastModifiedAt: now,
              lastModifiedBy: currentUser?.uid,
              lastModifiedByName: userName,
              activityLog: [
                ...currentLog,
                {
                  action: 'read',
                  timestamp: now,
                  userId: currentUser?.uid,
                  userName: userName,
                  userRole: userData.role,
                  details: 'Quotation opened and marked as read',
                  detailsKey: 'quotations.detailRead'
                }
              ]
            });
            console.log('Quotation status updated to read');
            data.status = 'read';
          } catch (updateError) {
            console.error('Error updating quotation status:', updateError);
          }
        }
        
        setQuotation({
          id: quotationDoc.id,
          ...data
        } as QuotationData);
      } catch (err) {
        console.error('Error fetching quotation:', err);
        setError('Error al cargar la cotización');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotation();
  }, [quotationId, userData?.businessId]);

  useEffect(() => {
    if (showClientModal && userData?.businessId) {
      const fetchClients = async () => {
        const db = getFirestore();
        const clientsRef = collection(db, `clients/${userData.businessId}/clients`);
        const querySnapshot = await getDocs(clientsRef);
        setClients(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      };
      fetchClients();
    }
  }, [showClientModal, userData?.businessId]);

  // Cargar servicios del negocio cuando se active el tab de servicios
  useEffect(() => {
    const fetchBusinessServices = async () => {
      if (!userData?.businessId) return;
      
      try {
        setLoadingBusinessServices(true);
        const db = getFirestore();
        const businessRef = doc(db, 'businesses', userData.businessId);
        const businessDoc = await getDoc(businessRef);
        
        if (businessDoc.exists()) {
          const businessData = businessDoc.data();
          if (businessData.services) {
            try {
              const parsedServices = JSON.parse(businessData.services);
              setBusinessServices(Array.isArray(parsedServices) ? parsedServices : []);
            } catch {
              setBusinessServices([]);
            }
          }
        }
      } catch (error) {
        console.error('Error loading business services:', error);
      } finally {
        setLoadingBusinessServices(false);
      }
    };

    fetchBusinessServices();
  }, [userData?.businessId]);

  // Cargar impuestos cuando se active el tab de propuesta
  useEffect(() => {
    if (activeTab === 'proposal' && userData?.businessId) {
      fetchBusinessTaxes();
    }
  }, [activeTab, userData?.businessId]);

  // Cargar datos de la propuesta cuando se cargue la cotización
  useEffect(() => {
    if (quotation) {
      // Cargar impuestos seleccionados
      if (quotation.selectedTaxes) {
        setSelectedTaxes(quotation.selectedTaxes);
      }
      
      // Cargar descuentos
      if (quotation.discountAmount !== undefined) {
        setDiscountAmount(quotation.discountAmount);
      }
      if (quotation.discountPercentage !== undefined) {
        setDiscountPercentage(quotation.discountPercentage);
      }
      
      // Cargar información del trabajo
      if (quotation.workDuration !== undefined) {
        setWorkDuration(quotation.workDuration);
      }
      if (quotation.workDurationType) {
        setWorkDurationType(quotation.workDurationType);
      }
      if (quotation.workStartDate) {
        setWorkStartDate(quotation.workStartDate);
      }
      if (quotation.quotationValidityDate) {
        setQuotationValidityDate(quotation.quotationValidityDate);
      }
    }
  }, [quotation]);

  const fetchBusinessTaxes = async () => {
    if (!userData?.businessId) return;
    
    try {
      const db = getFirestore();
      const businessRef = doc(db, 'businesses', userData.businessId);
      const businessDoc = await getDoc(businessRef);
      
      if (businessDoc.exists()) {
        const businessData = businessDoc.data();
        if (businessData.taxes) {
          try {
            const parsedTaxes = JSON.parse(businessData.taxes);
            setBusinessTaxes(Array.isArray(parsedTaxes) ? parsedTaxes : []);
          } catch {
            setBusinessTaxes([]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading business taxes:', error);
    }
  };

  const handleCreateProject = async () => {
    if (!quotation || !userData?.businessId) {
      toast.error('No se puede crear el proyecto en este momento');
      return;
    }

    setCreatingProject(true);

    try {
      // 1. Buscar si el cliente ya existe por email
      const clientsRef = collection(db, `clients/${userData.businessId}/clients`);
      const q = query(clientsRef, where('email', '==', quotation.clientEmail));
      const querySnapshot = await getDocs(q);
      let clientRef;
      if (!querySnapshot.empty) {
        // Cliente ya existe
        clientRef = doc(db, `clients/${userData.businessId}/clients/${querySnapshot.docs[0].id}`);
      } else {
        // Crear nuevo cliente
        clientRef = doc(collection(db, `clients/${userData.businessId}/clients`));
        const clientData = {
          name: quotation.clientName || 'Sin nombre',
          email: quotation.clientEmail || 'sin@email.com',
          phone: quotation.clientPhone || 'Sin teléfono',
          address: quotation.clientAddress || 'Sin dirección',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        await setDoc(clientRef, clientData);
      }

      // Calcular fechas de inicio y fin basadas en la duración del trabajo
      let startDate = null;
      let endDate = null;
      
      if (quotation.workStartDate && quotation.workDuration) {
        startDate = new Date(quotation.workStartDate);
        const durationInDays = quotation.workDurationType === 'hours' 
          ? Math.ceil(quotation.workDuration / 24) 
          : quotation.workDuration;
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + durationInDays);
      }

      // 2. Crear el proyecto en la colección projects
      const projectRef = doc(collection(db, `projects/${userData.businessId}/projects`));
      const projectData = {
        clientId: clientRef.id,
        quotationId: quotation.id,
        status: 'active',
        name: `Proyecto de ${quotation.clientName || 'Cliente'}`,
        description: quotation.description || '',
        services: quotation.services || [],
        materials: quotation.materials || [],
        
        // Campos de fechas y duración
        startDate: startDate ? serverTimestamp() : null,
        endDate: endDate ? serverTimestamp() : null,
        workDuration: quotation.workDuration || 0,
        workDurationType: quotation.workDurationType || 'days',
        workStartDate: quotation.workStartDate || null,
        
        // Campos de montos
        subtotal: quotation.subtotal || calculateSubtotal(),
        discount: quotation.discount || calculateDiscount(),
        subtotalWithDiscounts: (quotation.subtotal || calculateSubtotal()) - (quotation.discount || calculateDiscount()),
        taxes: quotation.taxes || calculateTaxes(),
        total: quotation.total || calculateTotal(),
        
        // Campos adicionales
        totalAmount: quotation.total || calculateTotal(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        progress: 0,
        notes: [],
        
        // Array con el PDF de la cotización
        quotationPDF: quotation.pdfUrl ? [quotation.pdfUrl] : []
      };

      await setDoc(projectRef, projectData);

      // 3. Actualizar el estado de la cotización a 'converted'
      const quotationRef = doc(db, `quotations/${userData.businessId}/submissions/${quotation.id}`);
      
      // Get current activity log
      const quotationDoc = await getDoc(quotationRef);
      const currentLog = quotationDoc.exists() ? (quotationDoc.data().activityLog || []) : [];
      
      const now = new Date();
      const userName = `${(userData as any).firstName || ''} ${(userData as any).lastName || ''}`.trim() || (userData as any).email || currentUser?.email;
      
      await updateDoc(quotationRef, {
        status: 'converted',
        projectId: projectRef.id,
        updatedAt: serverTimestamp(),
        lastModifiedAt: now,
        lastModifiedBy: currentUser?.uid,
        lastModifiedByName: userName,
        activityLog: [
          ...currentLog,
          {
            action: 'converted',
            timestamp: now,
            userId: currentUser?.uid,
            userName: userName,
            userRole: userData.role,
            details: `Quotation converted to project`,
            detailsKey: 'quotations.detailConverted',
            projectId: projectRef.id
          }
        ]
      });

      toast.success('Proyecto creado exitosamente');
      navigate(`/projects/${projectRef.id}`);
    } catch (error) {
      console.error('Error al crear el proyecto:', error);
      toast.error('Error al crear el proyecto');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData?.businessId || !newClient.name || !newClient.email) return;
    setCreatingClient(true);
    try {
      const db = getFirestore();
      const clientsRef = collection(db, `clients/${userData.businessId}/clients`);
      const docRef = await addDoc(clientsRef, {
        name: newClient.name,
        email: newClient.email,
        createdAt: new Date()
      });
      const client = { id: docRef.id, ...newClient };
      setClients(prev => [...prev, client]);
      setNewClient({ name: '', email: '' });
      // Usar la nueva función para asignar el cliente
      await handleSelectClient(client);
    } catch (error) {
      toast.error('Error al crear cliente');
    } finally {
      setCreatingClient(false);
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'unread':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'read':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sent':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'converted':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'approved':
        return 'Aprobada';
      case 'rejected':
        return 'Rechazada';
      case 'unread':
        return 'No leído';
      case 'read':
        return 'Leído';
      case 'sent':
        return 'Enviada';
      case 'converted':
        return 'Convertido a Proyecto';
      case 'draft':
        return 'Borrador';
      default:
        return status;
    }
  };

  const handleSelectClient = async (client: any) => {
    if (!quotation || !userData?.businessId) {
      toast.error('No se puede seleccionar el cliente en este momento');
      return;
    }

    try {
      const db = getFirestore();
      const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotation.id);
      
      await updateDoc(quotationRef, {
        clientName: client.name,
        clientEmail: client.email,
        clientId: client.id,
        status: 'pending',
        updatedAt: new Date()
      });

      // Actualizar el estado local
      setQuotation((prev: QuotationData | null) => prev ? { 
        ...prev, 
        clientName: client.name, 
        clientEmail: client.email,
        clientId: client.id,
        status: 'pending'
      } : null);
      
      setShowClientModal(false);
      toast.success('Cliente seleccionado correctamente');
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Error al seleccionar el cliente');
    }
  };

  const handleCreateClientAndAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData?.businessId || !newClient.name || !newClient.email) return;
    setCreatingClient(true);
    try {
      const db = getFirestore();
      const clientsRef = collection(db, `clients/${userData.businessId}/clients`);
      const docRef = await addDoc(clientsRef, {
        name: newClient.name,
        email: newClient.email,
        createdAt: new Date()
      });
      const client = { id: docRef.id, ...newClient };
      await handleSelectClient(client);
      setNewClient({ name: '', email: '' });
      setShowNewClientForm(false);
    } catch (error) {
      toast.error('Error al crear cliente');
    } finally {
      setCreatingClient(false);
    }
  };

  // Funciones para manejar servicios
  const handleOpenServicesModal = () => {
    // Inicializar con los servicios existentes de la cotización
    setSelectedServices(quotation?.services || []);
    setShowServicesModal(true);
  };

  const handleSelectExistingService = (service: any) => {
    const serviceWithQuantity = {
      ...service,
      quantity: 1,
      selectedFromBusiness: true
    };
    setSelectedServices((prev: any[]) => [...prev, serviceWithQuantity]);
  };

  const handleRemoveService = (index: number) => {
    setSelectedServices((prev: any[]) => prev.filter((_, i) => i !== index));
  };

  const handleAddNewService = () => {
    if (newService.name.trim()) {
      const serviceToAdd = {
        ...newService,
        id: Date.now().toString(),
        selectedFromBusiness: false
      };
      setSelectedServices((prev: any[]) => [...prev, serviceToAdd]);
      setNewService({
        name: '',
        description: '',
        price: '',
        priceType: 'fixed',
        quantity: 1
      });
      setIsAddingService(false);
    }
  };

  const handleSaveServices = async () => {
    if (!quotation || !userData?.businessId) {
      toast.error('No se puede guardar los servicios en este momento');
      return;
    }

    try {
      const db = getFirestore();
      const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotation.id);
      
      await updateDoc(quotationRef, {
        services: selectedServices,
        updatedAt: new Date()
      });

      // Actualizar el estado local
      setQuotation((prev: QuotationData | null) => prev ? { ...prev, services: selectedServices } : null);
      setShowServicesModal(false);
      toast.success('Servicios guardados correctamente');
    } catch (error) {
      console.error('Error saving services:', error);
      toast.error('Error al guardar los servicios');
    }
  };

  const handleUpdateServiceQuantity = (index: number, quantity: number) => {
    setSelectedServices((prev: any[]) => prev.map((service, i) => 
      i === index ? { ...service, quantity: Math.max(1, quantity) } : service
    ));
  };

  // Funciones para manejar materiales
  const handleOpenMaterialsModal = () => {
    // Inicializar con los materiales existentes de la cotización
    setSelectedMaterials(quotation?.materials || []);
    setShowMaterialsModal(true);
  };



  const handleRemoveMaterial = (index: number) => {
    setSelectedMaterials((prev: any[]) => prev.filter((_, i) => i !== index));
  };

  const handleAddNewMaterial = () => {
    if (newMaterial.name.trim()) {
      const materialToAdd = {
        ...newMaterial,
        id: Date.now().toString(),
        selectedFromBusiness: false
      };
      setSelectedMaterials((prev: any[]) => [...prev, materialToAdd]);
      setNewMaterial({
        name: '',
        description: '',
        price: '',
        priceType: 'fixed',
        quantity: 1,
        unit: 'unidad'
      });
      setIsAddingMaterial(false);
    }
  };

  const handleSaveMaterials = async () => {
    if (!quotation || !userData?.businessId) {
      toast.error('No se puede guardar los materiales en este momento');
      return;
    }

    try {
      const db = getFirestore();
      const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotation.id);
      
      await updateDoc(quotationRef, {
        materials: selectedMaterials,
        updatedAt: new Date()
      });

      // Actualizar el estado local
      setQuotation((prev: QuotationData | null) => prev ? { ...prev, materials: selectedMaterials } : null);
      setShowMaterialsModal(false);
      toast.success('Materiales guardados correctamente');
    } catch (error) {
      console.error('Error saving materials:', error);
      toast.error('Error al guardar los materiales');
    }
  };

  const handleUpdateMaterialQuantity = (index: number, quantity: number) => {
    setSelectedMaterials((prev: any[]) => prev.map((material, i) => 
      i === index ? { ...material, quantity: Math.max(1, quantity) } : material
    ));
  };

  // Funciones para manejar la propuesta
  const handleTaxSelection = (tax: any, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTaxes((prev: any[]) => [...prev, tax]);
    } else {
      setSelectedTaxes((prev: any[]) => prev.filter(t => t.id !== tax.id));
    }
  };

  const calculateSubtotal = () => {
    if (!quotation) return 0;
    
    let subtotal = 0;
    
    // Sumar servicios
    if (quotation.services && Array.isArray(quotation.services)) {
      quotation.services.forEach((service: any) => {
        const price = parseFloat(service.price) || 0;
        const quantity = service.quantity || 1;
        subtotal += price * quantity;
      });
    }
    
    // Sumar materiales
    if (quotation.materials && Array.isArray(quotation.materials)) {
      quotation.materials.forEach((material: any) => {
        const price = parseFloat(material.price) || 0;
        const quantity = material.quantity || 1;
        subtotal += price * quantity;
      });
    }
    
    return subtotal;
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    const amountDiscount = discountAmount || 0;
    const percentageDiscount = (subtotal * (discountPercentage || 0)) / 100;
    return Math.min(amountDiscount + percentageDiscount, subtotal);
  };

  const calculateTaxes = () => {
    const subtotalAfterDiscount = calculateSubtotal() - calculateDiscount();
    let totalTaxes = 0;
    
    selectedTaxes.forEach((tax: any) => {
      totalTaxes += (subtotalAfterDiscount * tax.percentage) / 100;
    });
    
    return totalTaxes;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const taxes = calculateTaxes();
    return subtotal - discount + taxes;
  };

  const handleSaveProposal = async () => {
    if (!quotation || !userData?.businessId) {
      toast.error('No se puede guardar la propuesta en este momento');
      return;
    }

    try {
      setIsSavingProposal(true);
      const db = getFirestore();
      const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotation.id);
      
      const proposalData = {
        selectedTaxes,
        discountAmount,
        discountPercentage,
        workDuration,
        workDurationType,
        workStartDate,
        quotationValidityDate,
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        taxes: calculateTaxes(),
        total: calculateTotal(),
        updatedAt: new Date()
      };
      
      await updateDoc(quotationRef, proposalData);

      // Actualizar el estado local
      setQuotation((prev: QuotationData | null) => prev ? { ...prev, ...proposalData } : null);
      toast.success('Propuesta guardada correctamente');
    } catch (error) {
      console.error('Error saving proposal:', error);
      toast.error('Error al guardar la propuesta');
    } finally {
      setIsSavingProposal(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!userData || !userData.businessId || !quotation || !quotation.id) {
      toast.error(t('proposal.pdfErrorData'));
      return;
    }
    try {
      // Obtener datos del negocio
      const businessProfile = await getBusinessProfile(currentUser?.uid || '');
      // Preparar datos del cliente
      const clientData = {
        clientName: quotation.clientName || '',
        clientEmail: quotation.clientEmail || '',
        customFields: quotation.customFields || []
      };
      // Preparar datos de la propuesta
      const proposalData = {
        services: quotation.services || [],
        materials: quotation.materials || [],
        selectedTaxes: quotation.selectedTaxes || [],
        discountAmount: quotation.discountAmount || 0,
        discountPercentage: quotation.discountPercentage || 0,
        workDuration: quotation.workDuration || 0,
        workDurationType: quotation.workDurationType || 'days',
        workStartDate: quotation.workStartDate || '',
        quotationValidityDate: quotation.quotationValidityDate || '',
        subtotal: quotation.subtotal || 0,
        discount: quotation.discount || 0,
        taxes: quotation.taxes || 0,
        total: quotation.total || 0,
      };
      // Generar y subir PDF, obtener URL
      const pdfUrl = await generateProposalPDF(
        businessProfile,
        clientData,
        proposalData,
        i18n.language,
        userData.businessId,
        quotation.id
      );
      // Guardar la URL en Firestore
      const db = getFirestore();
      const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotation.id);
      await updateDoc(quotationRef, { pdfUrl });
      // Actualizar el estado local
      setQuotation((prev: any) => prev ? { ...prev, pdfUrl } : prev);
      toast.success(t('proposal.pdfGenerated'));
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(t('errorGeneratingPDF'));
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-6">
              <button
                onClick={() => navigate('/quotations')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200 mr-4"
              >
                <FaArrowLeft className="mr-2" />
                Volver
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Cargando cotización...</h1>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center">
            <FaSpinner className="animate-spin text-4xl text-gray-400" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-6">
              <button
                onClick={() => navigate('/quotations')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200 mr-4"
              >
                <FaArrowLeft className="mr-2" />
                Volver
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Error</h1>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <FaExclamationCircle className="text-red-400 mr-3 text-xl" />
              <span className="text-red-800 font-medium">{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-6">
              <button
                onClick={() => navigate('/quotations')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200 mr-4"
              >
                <FaArrowLeft className="mr-2" />
                Volver
              </button>
              <h1 className="text-2xl font-bold text-gray-900">No se encontró la cotización</h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Eliminado: Nombre y correo del cliente, ya se muestran arriba */}
            {Array.isArray(quotation.customFields) && quotation.customFields.length > 0 && (
              quotation.customFields.map((field: any) => (
                <div key={field.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 uppercase tracking-wide">
                    {field.label}
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <span className="text-gray-900">{Array.isArray(field.value) ? field.value.join(', ') : field.value || t('notAvailable')}</span>
                  </div>
                </div>
              ))
            )}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 uppercase tracking-wide">
                {t('creationDate')}
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <span className="text-gray-900">
                  {quotation.createdAt instanceof Timestamp 
                    ? format(quotation.createdAt.toDate(), "d 'de' MMMM 'de' yyyy, h:mm:ss a", { locale: es })
                    : t('dateNotAvailable')}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 uppercase tracking-wide">
                {t('status')}
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeClasses(quotation.status)}`}>
                  {getStatusText(quotation.status)}
                </span>
              </div>
            </div>
          </div>
        );
      case 'services':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('requestedServices')}</h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {quotation.services && Array.isArray(quotation.services) 
                    ? `${quotation.services.length} ${quotation.services.length !== 1 ? t('services') : t('service')}`
                    : t('noServicesSpecified')
                  }
                </span>
                <button
                  onClick={handleOpenServicesModal}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <FaPlus className="text-sm" />
                  {t('editServices')}
                </button>
              </div>
            </div>
            
            {/* Servicios de la cotización */}
            {quotation.services && Array.isArray(quotation.services) && quotation.services.length > 0 ? (
              <div className="space-y-4">
                {quotation.services.map((service: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">
                          {service.name || `${t('service')} ${index + 1}`}
                        </h4>
                        {service.description && (
                          <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm">
                          {service.price && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {service.price} {service.priceType && (
                                <span className="ml-1">
                                  {service.priceType === 'hourly' ? t('perHour') : 
                                   service.priceType === 'daily' ? t('perDay') : t('fixed')}
                                </span>
                              )}
                            </span>
                          )}
                          {service.quantity && (
                            <span className="text-gray-500">
                              {t('quantity')}: {service.quantity}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noServicesSpecified')}</h3>
                <p className="text-gray-500">
                  {t('noServicesDescription')}
                </p>
              </div>
            )}

            {/* Campos personalizados relacionados con servicios */}
            {quotation.customFields && Array.isArray(quotation.customFields) && (
              <div className="mt-8">
                <h4 className="text-md font-semibold text-gray-900 mb-4">{t('additionalInfo')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quotation.customFields
                    .filter((field: any) => 
                      field.label && 
                      (field.label.toLowerCase().includes('servicio') || 
                       field.label.toLowerCase().includes('trabajo') ||
                       field.label.toLowerCase().includes('proyecto'))
                    )
                    .map((field: any, index: number) => (
                      <div key={field.id || index} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <label className="block text-sm font-medium text-blue-900 mb-1">
                          {field.label}
                        </label>
                        <div className="text-sm text-blue-800">
                          {Array.isArray(field.value) ? field.value.join(', ') : field.value || t('notSpecified')}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Resumen de costos si está disponible */}
            {quotation.totalAmount && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-900">{t('estimatedTotal')}:</span>
                  <span className="text-lg font-bold text-green-900">
                    ${quotation.totalAmount.toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Servicios disponibles del negocio */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-gray-900">{t('availableBusinessServices')}</h4>
                {loadingBusinessServices && (
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                    {t('loading')}
                  </div>
                )}
              </div>
              
              {businessServices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {businessServices.map((service: any, index: number) => (
                    <div key={service.id || index} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <h5 className="font-medium text-blue-900 text-sm mb-1">
                        {service.name}
                      </h5>
                      {service.description && (
                        <p className="text-xs text-blue-700 mb-2 line-clamp-2">{service.description}</p>
                      )}
                      {service.price && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {service.price} {service.priceType && (
                            <span className="ml-1">
                              {service.priceType === 'hourly' ? t('perHour') : 
                               service.priceType === 'daily' ? t('perDay') : t('fixed')}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : !loadingBusinessServices ? (
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-700">
                    {t('noBusinessServicesConfigured')}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        );
      case 'materials':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('requiredMaterials')}</h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {quotation.materials && Array.isArray(quotation.materials) 
                    ? `${quotation.materials.length} ${quotation.materials.length !== 1 ? t('materials') : t('material')}`
                    : t('noMaterialsSpecified')
                  }
                </span>
                <button
                  onClick={handleOpenMaterialsModal}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <FaPlus className="text-sm" />
                  {t('editMaterials')}
                </button>
              </div>
            </div>
            
            {/* Materiales de la cotización */}
            {quotation.materials && Array.isArray(quotation.materials) && quotation.materials.length > 0 ? (
              <div className="space-y-4">
                {quotation.materials.map((material: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">
                          {material.name || `${t('material')} ${index + 1}`}
                        </h4>
                        {material.description && (
                          <p className="text-sm text-gray-600 mb-2">{material.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm">
                          {material.price && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {material.price} {material.priceType && (
                                <span className="ml-1">
                                  {material.priceType === 'hourly' ? t('perHour') : 
                                   material.priceType === 'daily' ? t('perDay') : t('fixed')}
                                </span>
                              )}
                            </span>
                          )}
                          {material.quantity && (
                            <span className="text-gray-500">
                              {t('quantity')}: {material.quantity} {material.unit || t('unit')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noMaterialsSpecified')}</h3>
                <p className="text-gray-500">
                  {t('noMaterialsDescription')}
                </p>
              </div>
            )}

            {/* Campos personalizados relacionados con materiales */}
            {quotation.customFields && Array.isArray(quotation.customFields) && (
              <div className="mt-8">
                <h4 className="text-md font-semibold text-gray-900 mb-4">{t('additionalMaterialsInfo')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quotation.customFields
                    .filter((field: any) => 
                      field.label && 
                      (field.label.toLowerCase().includes('material') || 
                       field.label.toLowerCase().includes('equipo') ||
                       field.label.toLowerCase().includes('herramienta'))
                    )
                    .map((field: any, index: number) => (
                      <div key={field.id || index} className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <label className="block text-sm font-medium text-orange-900 mb-1">
                          {field.label}
                        </label>
                        <div className="text-sm text-orange-800">
                          {Array.isArray(field.value) ? field.value.join(', ') : field.value || t('notSpecified')}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Resumen de costos de materiales si está disponible */}
            {quotation.materialsTotal && (
              <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-orange-900">{t('materialsTotal')}:</span>
                  <span className="text-lg font-bold text-orange-900">
                    ${quotation.materialsTotal.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      case 'proposal':
        return (
          <div className="space-y-8">
            {/* Servicios y Materiales */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Servicios */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FaTools className="text-red-500" />
                  {t('services')}
                </h3>
                {quotation.services && Array.isArray(quotation.services) && quotation.services.length > 0 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-gray-700 mb-2">
                      <span>{t('name')}</span>
                      <span>{t('description')}</span>
                      <span>{t('quantity')}</span>
                      <span>{t('unitPrice')}</span>
                      <span>{t('total')}</span>
                    </div>
                    {quotation.services.map((service: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="grid grid-cols-5 gap-2 text-sm items-center">
                          <div className="font-medium text-gray-900">{service.name}</div>
                          <div>{service.description || '-'}</div>
                          <div className="text-center">{service.quantity || 1}</div>
                          <div className="text-right">${parseFloat(service.price || 0).toFixed(2)}</div>
                          <div className="text-right text-red-600">${((parseFloat(service.price || 0) * (service.quantity || 1))).toFixed(2)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaTools className="text-4xl mx-auto mb-4 text-gray-300" />
                    <p>{t('noServicesSpecified')}</p>
                  </div>
                )}
              </div>

              {/* Materiales */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FaBox className="text-orange-500" />
                  {t('materials')}
                </h3>
                {quotation.materials && Array.isArray(quotation.materials) && quotation.materials.length > 0 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-gray-700 mb-2">
                      <span>{t('name')}</span>
                      <span>{t('description')}</span>
                      <span>{t('quantity')}</span>
                      <span>{t('unitPrice')}</span>
                      <span>{t('total')}</span>
                    </div>
                    {quotation.materials.map((material: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="grid grid-cols-5 gap-2 text-sm items-center">
                          <div className="font-medium text-gray-900">{material.name}</div>
                          <div>{material.description || '-'}</div>
                          <div className="text-center">{material.quantity || 1} {t('unit')}</div>
                          <div className="text-right">${parseFloat(material.price || 0).toFixed(2)}</div>
                          <div className="text-right text-orange-600">${((parseFloat(material.price || 0) * (material.quantity || 1))).toFixed(2)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaBox className="text-4xl mx-auto mb-4 text-gray-300" />
                    <p>{t('noMaterialsSpecified')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Impuestos */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaCalculator className="text-blue-500" />
                {t('taxes')}
              </h3>
              {businessTaxes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {businessTaxes.map((tax: any) => (
                    <label key={tax.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTaxes.some(t => t.id === tax.id)}
                        onChange={(e) => handleTaxSelection(tax, e.target.checked)}
                        className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{tax.name}</div>
                        <div className="text-sm text-gray-500">{tax.percentage}%</div>
                        {tax.description && (
                          <div className="text-xs text-gray-400">{tax.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaCalculator className="text-4xl mx-auto mb-4 text-gray-300" />
                  <p>{t('noTaxesConfigured')}</p>
                </div>
              )}
            </div>

            {/* Descuentos */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaPercent className="text-green-500" />
                {t('discounts')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('discountAmount')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('discountPercentage')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Información del Trabajo */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaClock className="text-purple-500" />
                {t('proposal.workInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('proposal.workDuration')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      value={workDuration}
                      onChange={(e) => setWorkDuration(parseInt(e.target.value) || 0)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600"
                      placeholder="0"
                    />
                    <select
                      value={workDurationType}
                      onChange={(e) => setWorkDurationType(e.target.value as 'hours' | 'days')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600"
                    >
                      <option value="days">{t('proposal.days')}</option>
                      <option value="hours">{t('proposal.hours')}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('proposal.workStartDate')}
                  </label>
                  <input
                    type="date"
                    value={workStartDate}
                    onChange={(e) => setWorkStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('proposal.quotationValidityDate')}
                  </label>
                  <input
                    type="date"
                    value={quotationValidityDate}
                    onChange={(e) => setQuotationValidityDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600"
                  />
                </div>
              </div>
            </div>

            {/* Resumen de Costos */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaReceipt className="text-red-500" />
                {t('proposal.costSummary')}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('proposal.subtotal')}:</span>
                  <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('proposal.discounts')}:</span>
                  <span className="font-medium text-green-600">-${calculateDiscount().toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('proposal.subtotalWithDiscounts')}:</span>
                  <span className="font-medium">${(calculateSubtotal() - calculateDiscount()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('proposal.taxes')}:</span>
                  <span className="font-medium text-blue-600">${calculateTaxes().toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-t-2 border-gray-200">
                  <span className="text-lg font-semibold text-gray-900">{t('proposal.total')}:</span>
                  <span className="text-lg font-bold text-red-600">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Botones de Acción */}
            <div className="flex justify-end gap-4">
              {quotation.pdfUrl ? (
                <a
                  href={quotation.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <FaFilePdf />
                  {t('proposal.viewPDF')}
                </a>
              ) : (
                <button
                  onClick={handleGeneratePDF}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <FaFilePdf />
                  {t('proposal.generatePDF')}
                </button>
              )}
              <button
                onClick={handleSaveProposal}
                disabled={isSavingProposal}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              >
                {isSavingProposal ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('proposal.saving')}
                  </>
                ) : (
                  <>
                    <FaSave />
                    {t('proposal.saveProposal')}
                  </>
                )}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-6">
            <button
              onClick={() => navigate('/quotations')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200 mr-4"
            >
              <FaArrowLeft className="mr-2" />
              {t('back')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900 flex-1">{t('quotationDetails')}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Información del Cliente - Mostrar selector si está en draft sin cliente */}
        {quotation?.status === 'draft' && !quotation?.clientName ? (
          <div className="bg-white rounded-lg shadow-sm border border-blue-200 overflow-hidden">
            <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
              <h2 className="text-lg font-semibold text-blue-900 flex items-center">
                <FaUser className="mr-2 text-blue-500" />
                Selecciona un Cliente
              </h2>
            </div>
            <div className="p-6">
              <button
                onClick={() => setShowClientModal(true)}
                className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <FaSearch />
                Seleccionar o Crear Cliente
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FaUser className="mr-2 text-gray-500" />
                {t('clientInfo')}
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 uppercase tracking-wide">
                    {t('clientName')}
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <span className="text-gray-900">{quotation.clientName || t('notAvailable')}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 uppercase tracking-wide">
                    {t('clientEmail')}
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <span className="text-gray-900">{quotation.clientEmail || t('notAvailable')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detalles de la Cotización con Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('quotationDetails')}</h2>
          </div>
          
          {/* Tabs */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex flex-wrap items-center">
              <button
                onClick={() => setActiveTab('details')}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeTab === 'details'
                    ? 'bg-white text-gray-900 border-b-2 border-red-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FaFileAlt className="mr-2" />
                {t('detailsTab')}
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeTab === 'services'
                    ? 'bg-white text-gray-900 border-b-2 border-red-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FaTools className="mr-2" />
                {t('servicesTab')}
              </button>
              <button
                onClick={() => setActiveTab('materials')}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeTab === 'materials'
                    ? 'bg-white text-gray-900 border-b-2 border-red-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FaBox className="mr-2" />
                {t('materialsTab')}
              </button>
              <button
                onClick={() => setActiveTab('proposal')}
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeTab === 'proposal'
                    ? 'bg-white text-gray-900 border-b-2 border-red-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FaClipboardList className="mr-2" />
                {t('proposalTab')}
              </button>
              
              {/* Botones de acción */}
              <div className="ml-auto flex items-center gap-3">
                {/* Botón Mark as Sent (solo si está en estado read) */}
                {quotation.status === 'read' && (
                  <button
                    onClick={async () => {
                      if (!quotation || !userData?.businessId) return;
                      try {
                        const now = new Date();
                        const userName = `${(userData as any).firstName || ''} ${(userData as any).lastName || ''}`.trim() || (userData as any).email || currentUser?.email;
                        const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', quotation.id);
                        
                        // Get current activity log
                        const quotationDoc = await getDoc(quotationRef);
                        const currentLog = quotationDoc.exists() ? (quotationDoc.data().activityLog || []) : [];
                        
                        await updateDoc(quotationRef, {
                          status: 'sent',
                          sentAt: now,
                          lastModifiedAt: now,
                          lastModifiedBy: currentUser?.uid,
                          lastModifiedByName: userName,
                          activityLog: [
                            ...currentLog,
                            {
                              action: 'sent',
                              timestamp: now,
                              userId: currentUser?.uid,
                              userName: userName,
                              userRole: userData.role,
                              details: 'Quotation marked as sent to client',
                              detailsKey: 'quotations.detailSent'
                            }
                          ]
                        });
                        setQuotation((prev: QuotationData | null) => prev ? { ...prev, status: 'sent' } : null);
                        toast.success('Quotation marked as sent');
                      } catch (error) {
                        console.error('Error updating status:', error);
                        toast.error('Error updating status');
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold shadow transition-all duration-200 flex items-center gap-2"
                  >
                    <FaCheck />
                    Mark as Sent
                  </button>
                )}
                
                {/* Botón Crear Proyecto */}
                <button
                  onClick={handleCreateProject}
                  disabled={creatingProject}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-6 py-3 rounded-xl font-bold shadow transition-all duration-200 flex items-center gap-2"
                >
                  {creatingProject ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaPlus className="mr-2" />
                  )}
                  {creatingProject ? t('creating') : t('createProject')}
                </button>
              </div>
            </div>
          </div>
          
          {/* Contenido de los Tabs */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Modal de selección de cliente */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-3xl border border-white/20 animate-fadeIn">
            <button
              onClick={() => setShowClientModal(false)}
              className="absolute top-4 right-4 bg-transparent border-none text-gray-500 cursor-pointer p-2 flex items-center justify-center transition-colors duration-200 hover:text-red-600"
            >
              <FaTimes />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('quotations.selectClient')}</h2>
            <div className="mb-4 flex items-center gap-2">
              <FaSearch className="text-gray-400" />
              <input
                type="text"
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                placeholder={t('quotations.searchClient')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-base w-full"
              />
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 mb-4">
              {clients.filter(c =>
                c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                c.email?.toLowerCase().includes(clientSearch.toLowerCase())
              ).map(client => (
                <div key={client.id} className="flex items-center gap-4 py-3 cursor-pointer hover:bg-blue-50 px-2 rounded transition-all"
                  onClick={() => handleSelectClient(client)}
                >
                  <FaUser className="text-gray-400" />
                  <div>
                    <div className="font-semibold text-gray-900">{client.name}</div>
                    <div className="text-gray-500 text-sm">{client.email}</div>
                  </div>
                </div>
              ))}
              {clients.length === 0 && (
                <div className="text-gray-400 text-center py-8">{t('quotations.noClientsRegistered')}</div>
              )}
            </div>
            <button
              onClick={() => setShowNewClientForm(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium mb-4"
            >
              {t('quotations.createNewClient')}
            </button>
            {showNewClientForm && (
              <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                <input
                  type="text"
                  placeholder={t('quotations.clientName')}
                  value={newClient.name}
                  onChange={e => setNewClient({ ...newClient, name: e.target.value })}
                  required
                  className="px-3 py-2 border border-gray-300 rounded-lg text-base w-full"
                />
                <input
                  type="email"
                  placeholder={t('quotations.clientEmail')}
                  value={newClient.email}
                  onChange={e => setNewClient({ ...newClient, email: e.target.value })}
                  required
                  className="px-3 py-2 border border-gray-300 rounded-lg text-base w-full"
                />
                <button
                  type="submit"
                  disabled={creatingClient}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium w-full"
                >
                  {creatingClient ? t('quotations.creating') : t('quotations.createClient')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Modal de cotización */}
      {/* Modal de gestión de servicios */}
      {showServicesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-6xl max-h-[90vh] overflow-y-auto relative shadow-3xl border border-white/20 animate-fadeIn">
            <button
              onClick={() => setShowServicesModal(false)}
              className="absolute top-4 right-4 bg-transparent border-none text-gray-500 cursor-pointer p-2 flex items-center justify-center transition-colors duration-200 hover:text-red-600"
            >
              <FaTimes />
            </button>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestionar Servicios de la Cotización</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Panel izquierdo - Servicios seleccionados */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Servicios Seleccionados</h3>
                
                {selectedServices.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <p className="text-gray-500">No hay servicios seleccionados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedServices.map((service, index) => (
                      <div key={service.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            {service.name}
                            {service.selectedFromBusiness && (
                              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                Del negocio
                              </span>
                            )}
                          </h4>
                          <button
                            onClick={() => handleRemoveService(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <FaTimes />
                          </button>
                        </div>
                        
                        {service.description && (
                          <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                        )}
                        
                        <div className="flex items-center gap-4">
                          {service.price && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {service.price} {service.priceType && (
                                <span className="ml-1">
                                  {service.priceType === 'hourly' ? '/hora' : 
                                   service.priceType === 'daily' ? '/día' : 'fijo'}
                                </span>
                              )}
                            </span>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600">Cantidad:</label>
                            <input
                              type="number"
                              min="1"
                              value={service.quantity || 1}
                              onChange={(e) => handleUpdateServiceQuantity(index, parseInt(e.target.value) || 1)}
                              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-red-600"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Panel derecho - Servicios disponibles y añadir nuevo */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Servicios Disponibles del Negocio</h3>
                
                {businessServices.length > 0 ? (
                  <div className="space-y-2">
                    {businessServices.map((service, index) => (
                      <button
                        key={service.id || index}
                        onClick={() => handleSelectExistingService(service)}
                        className="w-full text-left bg-blue-50 hover:bg-blue-100 rounded-lg p-3 border border-blue-200 transition-colors duration-200"
                      >
                        <h4 className="font-medium text-blue-900 text-sm mb-1">{service.name}</h4>
                        {service.description && (
                          <p className="text-xs text-blue-700 mb-2">{service.description}</p>
                        )}
                        {service.price && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {service.price} {service.priceType && (
                              <span className="ml-1">
                                {service.priceType === 'hourly' ? '/hora' : 
                                 service.priceType === 'daily' ? '/día' : 'fijo'}
                              </span>
                            )}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-blue-700">No hay servicios configurados en el negocio</p>
                  </div>
                )}

                {/* Añadir nuevo servicio */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Añadir Servicio Personalizado</h4>
                  
                  {isAddingService ? (
                    <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                      <input
                        type="text"
                        placeholder="Nombre del servicio"
                        value={newService.name}
                        onChange={(e) => setNewService((prev: any) => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                      />
                      <textarea
                        placeholder="Descripción (opcional)"
                        value={newService.description}
                        onChange={(e) => setNewService((prev: any) => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        rows={2}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Precio"
                          value={newService.price}
                          onChange={(e) => setNewService((prev: any) => ({ ...prev, price: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        />
                        <select
                          value={newService.priceType}
                          onChange={(e) => setNewService((prev: any) => ({ ...prev, priceType: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        >
                          <option value="fixed">Fijo</option>
                          <option value="hourly">Por Hora</option>
                          <option value="daily">Por Día</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddNewService}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Añadir
                        </button>
                        <button
                          onClick={() => setIsAddingService(false)}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAddingService(true)}
                      className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      <FaPlus />
                      Añadir Servicio Personalizado
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowServicesModal(false)}
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveServices}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
              >
                Guardar Servicios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de gestión de materiales */}
      {showMaterialsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-6xl max-h-[90vh] overflow-y-auto relative shadow-3xl border border-white/20 animate-fadeIn">
            <button
              onClick={() => setShowMaterialsModal(false)}
              className="absolute top-4 right-4 bg-transparent border-none text-gray-500 cursor-pointer p-2 flex items-center justify-center transition-colors duration-200 hover:text-red-600"
            >
              <FaTimes />
            </button>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestionar Materiales de la Cotización</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Panel izquierdo - Materiales seleccionados */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Materiales Seleccionados</h3>
                
                {selectedMaterials.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <p className="text-gray-500">No hay materiales seleccionados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedMaterials.map((material, index) => (
                      <div key={material.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            {material.name}
                            {material.selectedFromBusiness && (
                              <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                Del negocio
                              </span>
                            )}
                          </h4>
                          <button
                            onClick={() => handleRemoveMaterial(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <FaTimes />
                          </button>
                        </div>
                        
                        {material.description && (
                          <p className="text-sm text-gray-600 mb-2">{material.description}</p>
                        )}
                        
                        <div className="flex items-center gap-4">
                          {material.price && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {material.price} {material.priceType && (
                                <span className="ml-1">
                                  {material.priceType === 'hourly' ? '/hora' : 
                                   material.priceType === 'daily' ? '/día' : 'fijo'}
                                </span>
                              )}
                            </span>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600">Cantidad:</label>
                            <input
                              type="number"
                              min="1"
                              value={material.quantity || 1}
                              onChange={(e) => handleUpdateMaterialQuantity(index, parseInt(e.target.value) || 1)}
                              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-red-600"
                            />
                            <span className="text-xs text-gray-500">
                              {material.unit || 'unidad'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Panel derecho - Añadir materiales personalizados */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Añadir Materiales Personalizados</h3>
                
                <div className="bg-orange-50 rounded-lg p-6 text-center">
                  <div className="text-orange-400 mb-3">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-orange-900 mb-2">Materiales Personalizados</h4>
                  <p className="text-sm text-orange-700 mb-4">
                    Añade materiales específicos para esta cotización. Cada material puede tener su propio precio, cantidad y unidad de medida.
                  </p>
                </div>

                {/* Añadir nuevo material */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Añadir Material Personalizado</h4>
                  
                  {isAddingMaterial ? (
                    <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                      <input
                        type="text"
                        placeholder="Nombre del material"
                        value={newMaterial.name}
                        onChange={(e) => setNewMaterial((prev: any) => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                      />
                      <textarea
                        placeholder="Descripción (opcional)"
                        value={newMaterial.description}
                        onChange={(e) => setNewMaterial((prev: any) => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        rows={2}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          placeholder="Precio"
                          value={newMaterial.price}
                          onChange={(e) => setNewMaterial((prev: any) => ({ ...prev, price: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        />
                        <select
                          value={newMaterial.priceType}
                          onChange={(e) => setNewMaterial((prev: any) => ({ ...prev, priceType: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        >
                          <option value="fixed">Fijo</option>
                          <option value="hourly">Por Hora</option>
                          <option value="daily">Por Día</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Unidad (kg, m, etc.)"
                          value={newMaterial.unit}
                          onChange={(e) => setNewMaterial((prev: any) => ({ ...prev, unit: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-600"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddNewMaterial}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Añadir
                        </button>
                        <button
                          onClick={() => setIsAddingMaterial(false)}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAddingMaterial(true)}
                      className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      <FaPlus />
                      Añadir Material Personalizado
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowMaterialsModal(false)}
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveMaterials}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
              >
                Guardar Materiales
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de selección de cliente para draft */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-3xl border border-white/20 animate-fadeIn">
            <button
              onClick={() => setShowClientModal(false)}
              className="absolute top-4 right-4 bg-transparent border-none text-gray-500 cursor-pointer p-2 flex items-center justify-center transition-colors duration-200 hover:text-red-600"
            >
              <FaTimes />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Selecciona un Cliente</h2>
            <div className="mb-4 flex items-center gap-2">
              <FaSearch className="text-gray-400" />
              <input
                type="text"
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                placeholder="Buscar cliente..."
                className="px-3 py-2 border border-gray-300 rounded-lg text-base w-full"
              />
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 mb-4">
              {clients.filter(c =>
                c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                c.email?.toLowerCase().includes(clientSearch.toLowerCase())
              ).map(client => (
                <div key={client.id} className="flex items-center gap-4 py-3 cursor-pointer hover:bg-blue-50 px-2 rounded transition-all"
                  onClick={() => handleSelectClient(client)}
                >
                  <FaUser className="text-gray-400" />
                  <div>
                    <div className="font-semibold text-gray-900">{client.name}</div>
                    <div className="text-gray-500 text-sm">{client.email}</div>
                  </div>
                </div>
              ))}
              {clients.length === 0 && (
                <div className="text-gray-400 text-center py-8">No hay clientes registrados.</div>
              )}
            </div>
            <div className="mt-4 text-center">
              {!showNewClientForm ? (
                <button
                  onClick={() => setShowNewClientForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                >
                  Crear Nuevo Cliente
                </button>
              ) : (
                <form onSubmit={handleCreateClientAndAssign} className="flex flex-col gap-3 items-center mt-2">
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={e => setNewClient(c => ({ ...c, name: e.target.value }))}
                    placeholder="Nombre del cliente"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-base w-64"
                    required
                  />
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={e => setNewClient(c => ({ ...c, email: e.target.value }))}
                    placeholder="Correo del cliente"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-base w-64"
                    required
                  />
                  <button
                    type="submit"
                    disabled={creatingClient}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                  >
                    {creatingClient ? 'Guardando...' : 'Crear y Asignar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewClientForm(false)}
                    className="text-gray-500 hover:text-gray-800 mt-2"
                  >
                    Cancelar
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationScreen; 